import type { Middleware, SWRConfiguration } from 'swr';

/**
 * The SWR options every repo read hook passes explicitly. `globalSwrDefaults` below
 * is what the layout's `SWRConfig` applies; this adds the retry opt-out on top.
 *
 * Background revalidation is off: this data is paged and filter-driven, so a
 * refetch on window focus or reconnect re-runs every loaded page — for the
 * infinite hooks, once per page — and can renumber rows under a user who is
 * mid-interaction. Reads refresh on key change or an explicit `mutate()`.
 *
 * Retries are off because the proxy surfaces 4xx as rejections; retrying a
 * rejected authorization or validation response only delays the error card.
 *
 * Passed explicitly rather than relied on from the provider, so a hook rendered
 * outside the app shell behaves the same as one inside it.
 */
export const swrDefaults = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  shouldRetryOnError: false,
} as const satisfies SWRConfiguration;

/**
 * The route `@auth0/nextjs-auth0`'s `useUser()` reads. It is configurable through
 * `NEXT_PUBLIC_PROFILE_ROUTE`; the key is compared by suffix so a Next base path
 * cannot break the match.
 */
const SESSION_PROFILE_ROUTE = process.env.NEXT_PUBLIC_PROFILE_ROUTE || '/auth/profile';

/**
 * Keeps the session profile on SWR's own background revalidation. `useUser()` passes
 * no options of its own, so under the app-wide defaults it would fetch once per page
 * load; a logout or advertiser switch in another tab would then leave this tab's
 * header and feature flags describing a session that no longer exists. Refetching the
 * profile on focus is what notices that, and it is a single small read.
 */
export const keepSessionProfileFresh: Middleware = (useSWRNext) => (key, fetcher, config) => {
  const isProfile = typeof key === 'string' && key.endsWith(SESSION_PROFILE_ROUTE);
  return useSWRNext(
    key,
    fetcher,
    isProfile ? { ...config, revalidateOnFocus: true, revalidateOnReconnect: true } : config,
  );
};

/**
 * What the app-wide `SWRConfig` applies.
 *
 * Retry is deliberately left at SWR's default here. The provider sits above the whole
 * tree, and `swr` is deduped, so it also reaches `useSWR` calls inside dependencies —
 * `@auth0/nextjs-auth0`'s `useUser()` reads `/auth/profile` through it with no options
 * of its own, and its fetcher throws on any non-ok response. Turning retry off there
 * would let one transient failure leave the session unresolved for the rest of the
 * page's life. Our own reads opt out of retry by passing `swrDefaults` explicitly.
 *
 * The same reach is why the session profile is handed back its focus revalidation
 * through `keepSessionProfileFresh`; every other read keeps the flags above.
 */
export const globalSwrDefaults = {
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
  use: [keepSessionProfileFresh],
} as const satisfies SWRConfiguration;
